import React from 'react';
import createStore from 'store/factory';

const AppState = createStore((state, action) => {
  const prev = state[action.type];
  if (typeof action.value === "function") {
    const value = action.value(prev);
    if (value !== prev) {
      return {...state, [action.type]: value};
    }
  } else if (action.value !== prev) {
    return {...state, [action.type]: action.value};
  }
  return state;
}, {});

const identity = value => value;

/* eslint-disable react-hooks/rules-of-hooks */

export default class EditingContext {
  static context = React.createContext(null);
  handlers = new Set();
  task = null;
  state = new AppState();

  addHandler(func) {
    this.handlers.add(func);
    if (this.task && func(this.task)) {
      this.task = null;
    }
  }
  removeHandler(func) {
    this.handlers.delete(func);
  }

  dispatch = task => {
    for (const func of this.handlers) {
      if (func(task)) {
        return;
      }
    }
    this.task = task;
  }

  static Provider({ context, children }) {
    return (
      <EditingContext.context.Provider value={context}>
        <AppState.Provider store={context.state}>
          {children}
        </AppState.Provider>
      </EditingContext.context.Provider>
    );
  }

  static useHandler(func) {
    const instance = React.useContext(this.context);
    React.useLayoutEffect(() => {
      instance.addHandler(func);
      return () => instance.removeHandler(func);
    }, [instance, func]);
  }

  static useDispatch() {
    const instance = React.useContext(this.context);
    return instance.dispatch;
  }

  static useState(key, initialValue) {
    const initialRef = React.useRef(initialValue);
    const dispatch = AppState.useDispatch();
    const state = AppState.useSelector(state => state[key]);
    if (state === undefined) {
      if (typeof initialRef.current === "function") {
        initialRef.current = initialRef.current();
      }
    }
    React.useEffect(() => {
      if (state === undefined) {
        dispatch({type: key, value: initialRef.current});
      }
    }, [state, key, dispatch]);
    const setState = React.useCallback(value => dispatch({type: key, value}), [key, dispatch]);
    return [state === undefined ? initialRef.current : state, setState];
  }

  static useStateView(key, func=identity) {
    return AppState.useSelector(state => func(state[key]), [key, func]);
  }

  static useStateSetter(key) {
    const dispatch = AppState.useDispatch();
    return React.useCallback(value => dispatch({type: key, value}), [key, dispatch]);
  }
}

/* eslint-enable react-hooks/rules-of-hooks */
